import React, { useState } from 'react'
import { IconDeviceFloppy } from '@tabler/icons-react'
import classNames from 'classnames'

import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { Pill } from '@/components/pills/Pill'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'
import { TextInput } from '@/components/forms/TextInput'
import {
  selectedModelBlocksSave,
  MODEL_DEPRECATED_MESSAGE,
} from '@/components/forms/formik/selects/modelGating'

import { StagedChangesBanner } from './StagedChangesBanner'

/**
 * Component for viewing and editing prompt content
 */
export const PromptEditor = ({
  prompt,
  editedValues,
  setEditedValue,
  stagedChanges,
  isEditing,
  hasChanges,
  handleStageChanges,
  handleCancelEdit,
  onClearStagedChanges,
  onSaveToScorecard,
  savingToScorecard,
  qaCopilotModelOptions,
  canEdit = true,
  setIsEditing,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!prompt) {
    return null
  }

  if (isEditing) {
    // Without `$transcript` the call's transcript never reaches the LLM and it hallucinates.
    // Only enforce on edits to user_prompt itself so unrelated field edits aren't blocked
    // on legacy prompts that were saved before this validation existed.
    const userPromptEdited = editedValues.userPrompt !== prompt.user_prompt
    const userPromptMissingTranscript =
      userPromptEdited && !(editedValues.userPrompt || '').includes('$transcript')
    const silenceThresholdTooLow =
      Number.isFinite(editedValues.silenceThresholdSeconds) &&
      editedValues.silenceThresholdSeconds < 5

    // Edit-only surface: a deprecated model can't be saved (the server 422s), so block
    // staging a change while one is selected.
    const modelIsDeprecated = selectedModelBlocksSave(qaCopilotModelOptions, editedValues.model)

    return (
      <div className="prompt-card prompt-card-editing">
        <div className="prompt-edit-form">
          <div className="prompt-edit-textarea-section">
            <FormField>
              <TextArea
                value={editedValues.userPrompt}
                onChange={(event) => setEditedValue('userPrompt', event.target.value)}
                placeholder="Enter user prompt..."
                rows={12}
                data-testid="edit-user-prompt"
                aria-invalid={userPromptMissingTranscript}
                aria-describedby={
                  userPromptMissingTranscript ? 'prompt-transcript-error' : undefined
                }
              />
              {userPromptMissingTranscript && (
                <span
                  id="prompt-transcript-error"
                  role="alert"
                  className="prompt-validation-error"
                  data-testid="prompt-transcript-validation-error"
                >
                  AI prompt must include &apos;$transcript&apos;
                </span>
              )}
            </FormField>
          </div>

          <div className="prompt-edit-settings-section">
            <div className="prompt-settings-grid">
              <FormField>
                <label>AI Model</label>
                <Select
                  options={qaCopilotModelOptions}
                  value={editedValues.model}
                  onChange={(option) => setEditedValue('model', option?.value || null)}
                  placeholder="Select model..."
                  isClearable
                  data-testid="edit-model"
                />
                {modelIsDeprecated && (
                  <span
                    id="model-upgrade-error"
                    role="alert"
                    className="prompt-validation-error"
                    data-testid="model-upgrade-validation-error"
                  >
                    {MODEL_DEPRECATED_MESSAGE}
                  </span>
                )}
              </FormField>

              <FormField>
                <label>Transcript Side</label>
                <Select
                  options={[
                    { value: 'both', label: 'Both' },
                    { value: 'agent', label: 'Agent Only' },
                    { value: 'customer', label: 'Customer Only' },
                  ]}
                  value={editedValues.side}
                  onChange={(option) => setEditedValue('side', option?.value || 'both')}
                  placeholder="Select side..."
                  isClearable={false}
                  data-testid="edit-side"
                />
              </FormField>

              <FormField>
                <label>First X Minutes</label>
                <TextInput
                  type="number"
                  value={editedValues.firstMinutes || ''}
                  onChange={(event) =>
                    setEditedValue(
                      'firstMinutes',
                      event.target.value ? Number(event.target.value) : null
                    )
                  }
                  placeholder="Minutes at start"
                  min={0}
                  data-testid="edit-first-minutes"
                />
              </FormField>

              <FormField>
                <label>Last X Minutes</label>
                <TextInput
                  type="number"
                  value={editedValues.lastMinutes || ''}
                  onChange={(event) =>
                    setEditedValue(
                      'lastMinutes',
                      event.target.value ? Number(event.target.value) : null
                    )
                  }
                  placeholder="Minutes at end"
                  min={0}
                  data-testid="edit-last-minutes"
                />
              </FormField>

              <FormField>
                <label>Include Timestamps</label>
                <Select
                  options={[
                    { value: false, label: 'No' },
                    { value: true, label: 'Yes' },
                  ]}
                  value={editedValues.includeTimestamps}
                  onChange={(option) => setEditedValue('includeTimestamps', option?.value ?? false)}
                  isClearable={false}
                  data-testid="edit-include-timestamps"
                />
              </FormField>

              <FormField>
                <label>Silence Threshold (sec)</label>
                <TextInput
                  type="number"
                  value={editedValues.silenceThresholdSeconds || ''}
                  onChange={(event) =>
                    setEditedValue(
                      'silenceThresholdSeconds',
                      event.target.value ? Number(event.target.value) : null
                    )
                  }
                  placeholder="Min 5; blank = off"
                  min={5}
                  data-testid="edit-silence-threshold"
                  aria-invalid={silenceThresholdTooLow}
                  aria-describedby={silenceThresholdTooLow ? 'silence-threshold-error' : undefined}
                />
                {silenceThresholdTooLow && (
                  <span
                    id="silence-threshold-error"
                    role="alert"
                    className="prompt-validation-error"
                    data-testid="silence-threshold-validation-error"
                  >
                    Silence threshold must be at least 5 seconds
                  </span>
                )}
              </FormField>
            </div>
          </div>

          <div className="prompt-edit-footer">
            {hasChanges && (
              <span className="unsaved-changes-warning">
                You have unsaved changes. Test before staging.
              </span>
            )}
            <Button compact onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button
              primary
              compact
              onClick={handleStageChanges}
              disabled={
                !hasChanges ||
                userPromptMissingTranscript ||
                silenceThresholdTooLow ||
                modelIsDeprecated
              }
            >
              <IconDeviceFloppy size={16} /> Stage Changes
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const promptText = (stagedChanges?.user_prompt ?? prompt?.user_prompt) || 'No user prompt'
  const displayPrompt = stagedChanges || prompt

  const modelLabel =
    qaCopilotModelOptions.find((option) => option.value === displayPrompt.llm_model_id)?.label ||
    displayPrompt.llm_model_id ||
    'Default'

  return (
    <div className="prompt-card">
      <div className="prompt-card-header">
        <div className="prompt-metadata-pills">
          <Pill xs>{modelLabel}</Pill>
          {displayPrompt.side && (
            <Pill xs>
              {displayPrompt.side.charAt(0).toUpperCase() + displayPrompt.side.slice(1)}
            </Pill>
          )}
          {displayPrompt.first_x_minutes && (
            <Pill xs>First: {displayPrompt.first_x_minutes}min</Pill>
          )}
          {displayPrompt.last_x_minutes && <Pill xs>Last: {displayPrompt.last_x_minutes}min</Pill>}
          {displayPrompt.include_timestamps && <Pill xs>Timestamps</Pill>}
          {displayPrompt.silence_threshold_seconds && (
            <Pill xs>Silence: {displayPrompt.silence_threshold_seconds}s</Pill>
          )}
        </div>
        {canEdit && (
          <Button small onClick={() => setIsEditing(true)} data-testid="edit-prompt-button">
            Edit Prompt
          </Button>
        )}
      </div>

      <div className="prompt-card-content">
        <pre className={classNames('prompt-text', { truncated: !isExpanded })}>{promptText}</pre>
        {!isExpanded && (
          <button className="show-more-link" onClick={() => setIsExpanded(true)} type="button">
            Show more
          </button>
        )}
      </div>

      {stagedChanges && (
        <StagedChangesBanner
          onClear={onClearStagedChanges}
          onSaveToScorecard={onSaveToScorecard}
          savingToScorecard={savingToScorecard}
        />
      )}
    </div>
  )
}
